import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Gauge, OctagonX, Timer, ClipboardList, Ban, Activity } from "lucide-react";
import { get } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ONGLETS, toParams, useAnalyseFiltres } from "@/lib/analyse";
import { fmtDate, fmtDuree, fmtNum, fmtPct } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { KpiTile } from "@/components/common/KpiTile";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { FiltresUniversels } from "@/components/analyse/FiltresUniversels";
import { BarreExports } from "@/components/analyse/BarreExports";
import { KpiQuantite, TableauUnites, SelecteurUnite } from "@/components/analyse/Unites";
import { Classement, GrapheLignes, GraphePareto, GrapheQuantites, GrapheTemps } from "@/components/analyse/Graphes";
import { TableauDetail } from "@/components/analyse/TableauDetail";
import { cn } from "@/lib/utils";

const useAnalyse = (chemin, filtres, entrepriseId, ready, extra = {}) =>
  useQuery({
    queryKey: ["analyse", chemin, toParams(filtres), extra, entrepriseId],
    queryFn: () => get(`/analyse/${chemin}`, { ...toParams(filtres), ...extra }),
    enabled: ready, keepPreviousData: true,
  });

const TopSelect = ({ valeur, onChange }) => (
  <select data-testid="pareto-top" value={valeur} onChange={(e) => onChange(e.target.value)}
    className="h-6 bg-input border border-border rounded-sm text-[11px] px-1.5">
    <option value="5">Top 5</option><option value="10">Top 10</option><option value="tout">Tout</option>
  </select>
);

export default function AnalysePage() {
  const { entrepriseId, user } = useAuth();
  const { filtres, update, reset } = useAnalyseFiltres();
  const [onglet, setOnglet] = useState(() => new URLSearchParams(window.location.search).get("tab") || "vue");
  const [uniteGraphe, setUniteGraphe] = useState(null);
  const [top, setTop] = useState("5");
  const [page, setPage] = useState(1);
  const [recherche, setRecherche] = useState("");
  const ready = user.role !== "superadmin" || !!entrepriseId;

  useEffect(() => {
    update({ ligne_id: "", produit_id: "", famille: "", quart_id: "", equipe: "", superviseur: "",
             operateur_id: "", of_id: "", categorie_arret_id: "", motif_rejet_id: "", unite: "" });
  }, [entrepriseId, update]);

  const changerOnglet = (k) => {
    setOnglet(k); setPage(1); setRecherche("");
    const url = new URL(window.location);
    url.searchParams.set("tab", k);
    window.history.replaceState({}, "", url);
  };

  const vue = useAnalyse("vue", filtres, entrepriseId, ready && (onglet === "vue"));
  const prod = useAnalyse("production", filtres, entrepriseId, ready && onglet === "production");
  const trs = useAnalyse("trs", filtres, entrepriseId, ready && onglet === "trs");
  const arrets = useAnalyse("arrets", filtres, entrepriseId, ready && onglet === "arrets");
  const rejets = useAnalyse("rejets", filtres, entrepriseId, ready && onglet === "rejets");
  const ofs = useAnalyse("of", filtres, entrepriseId, ready && onglet === "of");
  const detailArrets = useAnalyse("arrets/detail", filtres, entrepriseId, ready && onglet === "arrets",
    { page, page_size: 25, ...(recherche ? { recherche } : {}) });
  const detailRejets = useAnalyse("rejets/detail", filtres, entrepriseId, ready && onglet === "rejets",
    { page, page_size: 25, ...(recherche ? { recherche } : {}) });

  const q = vue.data?.quantites || prod.data?.quantites || rejets.data?.quantites;
  const unites = q?.unites || [];
  const unite = uniteGraphe && unites.includes(uniteGraphe) ? uniteGraphe : unites[0];
  const courant = { vue, production: prod, trs, arrets, rejets, of: ofs }[onglet];

  const drill = (cle, valeur) => { update({ [cle]: String(valeur) }); setPage(1); };

  return (
    <div data-testid="analyse-page" className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: "Analyse & exports" }]}
        title="Centre d'analyse & exports"
        subtitle={`Filtrer → visualiser → analyser → exporter · ${fmtDate(filtres.du)} → ${fmtDate(filtres.au)}`}
        actions={<BarreExports filtres={filtres} onglet={onglet} />} />

      {!ready ? <EmptyState title="Sélectionnez une entreprise" hint="En tant que superadmin, choisissez l'entreprise à analyser dans la barre supérieure." /> : (<>
        <FiltresUniversels filtres={filtres} update={update} reset={reset} onglet={onglet} />

        <div data-testid="analyse-onglets" className="panel px-1.5 py-1.5 flex flex-wrap gap-1 overflow-x-auto">
          {ONGLETS.map((o) => (
            <button key={o.key} data-testid={`onglet-${o.key}`} onClick={() => changerOnglet(o.key)}
              className={cn("h-7 px-3 text-[11px] font-semibold rounded-sm whitespace-nowrap transition-colors",
                onglet === o.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground")}>
              {o.label}
            </button>
          ))}
        </div>

        {courant?.isLoading ? <LoadingState rows={6} /> : courant?.error ? <ErrorState error={courant.error} onRetry={courant.refetch} /> : (<>

          {onglet === "vue" && vue.data && (<>
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2">
              <KpiTile testId="kpi-trs" label="TRS" value={fmtPct(vue.data.trs)} trs={vue.data.trs} icon={Gauge}
                sub={`${vue.data.nb_of} OF · temps requis ${fmtDuree(vue.data.temps_requis_sec)}`} className="col-span-2" />
              <KpiTile testId="kpi-dispo" label="Disponibilité" value={fmtPct(vue.data.disponibilite)} trs={vue.data.disponibilite} />
              <KpiTile testId="kpi-perf" label="Performance" value={fmtPct(vue.data.performance)} trs={vue.data.performance} />
              <KpiTile testId="kpi-qualite" label="Qualité" value={fmtPct(vue.data.qualite)} trs={vue.data.qualite} />
              <KpiQuantite testId="kpi-production" q={vue.data.quantites} sub="Unités jamais additionnées entre elles" />
              <KpiQuantite testId="kpi-rejets" q={vue.data.quantites} cle="rejets" label="Production rejetée" />
              <KpiTile testId="kpi-temps-arret" label="Temps d'arrêt total" value={fmtDuree(vue.data.temps_arret_sec)} icon={Timer}
                sub={`Non planifié ${fmtDuree(vue.data.arrets_non_planifies_sec)}`} />
              <KpiTile testId="kpi-nb-arrets" label="Arrêts" value={fmtNum(vue.data.nb_arrets)} unit="évts" icon={OctagonX}
                sub={`Moyenne ${fmtDuree(vue.data.duree_moyenne_arret_sec)} · max ${fmtDuree(vue.data.arret_plus_long_sec)}`} />
              <KpiTile testId="kpi-of" label="OF" value={fmtNum(vue.data.nb_of)} icon={ClipboardList}
                sub={`${vue.data.nb_of_clos} terminés · ${vue.data.nb_of_actifs} actifs · ${vue.data.nb_of_prevus} prévus`} />
            </div>
            <Panel title="Production et objectifs par unité" testId="panel-unites" bodyClassName="p-0">
              <TableauUnites q={vue.data.quantites} />
            </Panel>
          </>)}

          {onglet === "production" && prod.data && (<>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <KpiQuantite testId="kpi-prod-bonne" q={prod.data.quantites} />
              <KpiQuantite testId="kpi-prod-rejets" q={prod.data.quantites} cle="rejets" label="Production rejetée" />
              <KpiQuantite testId="kpi-prod-objectif" q={prod.data.quantites} cle="objectif" label="Objectif" />
              <KpiQuantite testId="kpi-prod-theorique" q={prod.data.quantites} cle="theorique" label="Théorique (cadence)" />
            </div>
            <Panel title="Production et objectifs par unité" testId="panel-prod-unites" bodyClassName="p-0">
              <TableauUnites q={prod.data.quantites} />
            </Panel>
            {unite && <GrapheQuantites testId="graphe-production" titre={`Production par ${prod.data.granularite}`}
              serie={prod.data.serie} unite={unite}
              actions={<SelecteurUnite unites={unites} valeur={unite} onChange={setUniteGraphe} />} />}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              {[["par_machine", "Production par machine"], ["par_produit", "Production par produit"],
                ["par_quart", "Production par quart"], ["par_of", "Production par OF"]].map(([cle, titre]) => (
                <Classement key={cle} testId={`classement-${cle}`} titre={titre} cleValeur="valeur" format={fmtNum}
                  data={(prod.data[cle] || []).map((e) => ({ nom: e.nom, valeur: e.par_unite?.[unite]?.bonnes ?? 0 }))
                    .filter((e) => e.valeur > 0).sort((a, b) => b.valeur - a.valeur).slice(0, 12)} />
              ))}
            </div>
          </>)}

          {onglet === "trs" && trs.data && (<>
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2">
              <KpiTile testId="kpi-trs-global" label="TRS" value={fmtPct(trs.data.global.trs)} trs={trs.data.global.trs} icon={Gauge} className="col-span-2" />
              <KpiTile testId="kpi-trs-d" label="Disponibilité" value={fmtPct(trs.data.global.disponibilite)} trs={trs.data.global.disponibilite} />
              <KpiTile testId="kpi-trs-p" label="Performance" value={fmtPct(trs.data.global.performance)} trs={trs.data.global.performance} />
              <KpiTile testId="kpi-trs-q" label="Qualité" value={fmtPct(trs.data.global.qualite)} trs={trs.data.global.qualite} />
              <KpiTile testId="kpi-trs-temps" label="Temps requis" value={fmtDuree(trs.data.global.temps_requis_sec)} icon={Activity}
                sub={`Fonctionnement ${fmtDuree(trs.data.global.temps_fonctionnement_sec)}`} />
            </div>
            <GrapheLignes testId="graphe-trs" titre={`Évolution du TRS par ${trs.data.granularite}`} data={trs.data.serie}
              series={[{ cle: "trs", label: "TRS" }, { cle: "disponibilite", label: "Disponibilité" },
                       { cle: "performance", label: "Performance" }, { cle: "qualite", label: "Qualité" }]} />
            {[["par_machine", "TRS par machine"], ["par_quart", "TRS par quart"],
              ["par_produit", "TRS par produit"], ["par_of", "TRS par OF"]].map(([cle, titre]) => (
              <Panel key={cle} title={titre} testId={`table-${cle}`} bodyClassName="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs whitespace-nowrap">
                    <thead className="bg-muted/40"><tr className="text-left eyebrow">
                      <th className="px-2 py-1.5">Libellé</th><th className="px-2 py-1.5 text-right">OF</th>
                      <th className="px-2 py-1.5 text-right">D</th><th className="px-2 py-1.5 text-right">P</th>
                      <th className="px-2 py-1.5 text-right">Q</th><th className="px-2 py-1.5 text-right">TRS</th>
                      <th className="px-2 py-1.5 text-right">Temps requis</th><th className="px-2 py-1.5 text-right">Temps fonct.</th>
                    </tr></thead>
                    <tbody className="divide-y divide-border/50 font-mono">
                      {(trs.data[cle] || []).slice(0, 25).map((e) => (
                        <tr key={e.nom} className="hover:bg-muted/20">
                          <td className="px-2 py-1.5">{e.nom}</td>
                          <td className="px-2 py-1.5 text-right">{e.nb_of}</td>
                          <td className="px-2 py-1.5 text-right">{fmtPct(e.disponibilite)}</td>
                          <td className="px-2 py-1.5 text-right">{fmtPct(e.performance)}</td>
                          <td className="px-2 py-1.5 text-right">{fmtPct(e.qualite)}</td>
                          <td className="px-2 py-1.5 text-right font-semibold">{fmtPct(e.trs)}</td>
                          <td className="px-2 py-1.5 text-right text-muted-foreground">{fmtDuree(e.temps_requis_sec)}</td>
                          <td className="px-2 py-1.5 text-right text-muted-foreground">{fmtDuree(e.temps_fonctionnement_sec)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            ))}
          </>)}

          {onglet === "arrets" && arrets.data && (<>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
              <KpiTile testId="kpi-arret-total" label="Temps d'arrêt total" value={fmtDuree(arrets.data.kpi.temps_arret_sec)} icon={Timer} className="col-span-2" />
              <KpiTile testId="kpi-arret-nb" label="Nombre d'arrêts" value={fmtNum(arrets.data.kpi.nb_arrets)} unit="évts" icon={OctagonX}
                sub={arrets.data.kpi.nb_en_cours ? `${arrets.data.kpi.nb_en_cours} en cours` : null} />
              <KpiTile testId="kpi-arret-moyenne" label="Durée moyenne" value={fmtDuree(arrets.data.kpi.duree_moyenne_sec)} />
              <KpiTile testId="kpi-arret-max" label="Arrêt le plus long" value={fmtDuree(arrets.data.kpi.arret_plus_long_sec)} />
              <KpiTile testId="kpi-arret-dispo" label="Disponibilité" value={fmtPct(arrets.data.kpi.disponibilite)} trs={arrets.data.kpi.disponibilite}
                sub={`Temps requis ${fmtDuree(arrets.data.kpi.temps_requis_sec)}`} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[["planifies", "Arrêts planifiés", "planifie"], ["non_planifies", "Arrêts non planifiés", "non_planifie"]].map(([cle, titre, val]) => (
                <button key={cle} data-testid={`bloc-${cle}`} onClick={() => drill("type_arret", val)}
                  className="panel p-2.5 text-left hover:border-primary/50 transition-colors">
                  <span className="eyebrow">{titre}</span>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <span className="text-xl font-mono font-semibold">{fmtDuree(arrets.data[cle].temps_sec)}</span>
                    <span className="text-[11px] font-mono text-muted-foreground">
                      {arrets.data[cle].nb} arrêts · {arrets.data[cle].pct_du_temps_arret} % du temps d'arrêt
                    </span>
                  </div>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-3">
              <GraphePareto testId="pareto-arrets" titre="Pareto des causes d'arrêt (catégories)"
                data={arrets.data.pareto_causes} cleValeur="duree_sec" top={top === "tout" ? "tout" : Number(top)}
                actions={<TopSelect valeur={top} onChange={setTop} />}
                selection={null} onClic={(e) => e?.id && drill("categorie_arret_id", e.id)} />
              <Classement testId="arrets-par-machine" titre="Temps d'arrêt par machine" data={arrets.data.par_machine}
                cleValeur="duree_sec" onClic={(e) => drill("ligne_id", e.id)} selection={null} />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-3">
              <GrapheTemps testId="graphe-arrets" titre={`Évolution des arrêts par ${arrets.data.granularite}`} data={arrets.data.serie}
                series={[{ cle: "non_planifie_sec", label: "Non planifié", stack: "a", couleur: "#e02424" },
                         { cle: "planifie_sec", label: "Planifié", stack: "a", couleur: "#0891b2" }]} />
              <Classement testId="arrets-par-quart" titre="Temps d'arrêt par quart" data={arrets.data.par_quart}
                cleValeur="duree_sec" onClic={(e) => drill("quart_id", e.id)} />
            </div>
            <TableauDetail testId="table-arrets" titre="Arrêts détaillés" data={detailArrets.data}
              page={page} setPage={setPage} recherche={recherche} setRecherche={setRecherche}
              colonnes={[
                { cle: "date", label: "Date" }, { cle: "ligne", label: "Machine" }, { cle: "of", label: "OF" },
                { cle: "debut", label: "Début" }, { cle: "fin", label: "Fin", render: (i) => i.fin || "en cours" },
                { cle: "duree_sec", label: "Durée", align: "right", render: (i) => fmtDuree(i.duree_sec) },
                { cle: "categorie", label: "Cause (catégorie)" },
                { cle: "planifie", label: "Type", render: (i) => (i.planifie ? "Planifié" : "Non planifié") },
                { cle: "quart", label: "Quart" }, { cle: "operateur", label: "Opérateur" },
                { cle: "commentaire", label: "Commentaire" }]} />
          </>)}

          {onglet === "rejets" && rejets.data && (<>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <KpiQuantite testId="kpi-rejet-qte" q={rejets.data.quantites} cle="rejets" label="Quantité rejetée" />
              <KpiTile testId="kpi-rejet-taux" label="Taux de rejet" icon={Ban}
                value={rejets.data.quantites.par_unite?.length === 1 ? fmtPct(rejets.data.quantites.par_unite[0].taux_rejet_pct) : "par unité"}
                sub={rejets.data.quantites.par_unite?.map((e) => `${e.unite} ${e.taux_rejet_pct ?? "—"} %`).join(" · ")} />
              <KpiTile testId="kpi-rejet-nb" label="Événements de rejet" value={fmtNum(rejets.data.nb_evenements)} unit="évts" />
              <KpiTile testId="kpi-rejet-motif" label="Motif principal" value={rejets.data.motif_principal || "—"} />
            </div>
            <Panel title="Rejets par unité" testId="panel-rejets-unites" bodyClassName="p-0">
              <TableauUnites q={rejets.data.quantites} />
            </Panel>
            <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-3">
              {unite && (
                <GraphePareto testId="pareto-rejets" titre={`Pareto des motifs de rejet (${unite})`}
                  data={rejets.data.pareto_motifs?.[unite] || []} cleValeur="quantite" format={fmtNum}
                  top={top === "tout" ? "tout" : Number(top)}
                  actions={<div className="flex items-center gap-2"><SelecteurUnite unites={unites} valeur={unite} onChange={setUniteGraphe} /><TopSelect valeur={top} onChange={setTop} /></div>}
                  onClic={(e) => e?.id && drill("motif_rejet_id", e.id)} />
              )}
              <Classement testId="rejets-par-machine" titre="Rejets par machine" cleValeur="valeur" format={fmtNum}
                data={(rejets.data.par_machine || []).map((e) => ({ id: e.id, nom: e.nom, valeur: e.par_unite?.[unite] ?? 0, occurrences: e.occurrences }))} />
            </div>
            {unite && <GrapheQuantites testId="graphe-rejets" titre={`Rejets par ${rejets.data.granularite} (${unite})`}
              serie={rejets.data.serie.map((p) => ({ label: p.label, par_unite: { [unite]: { bonnes: 0, rejets: p.par_unite?.[unite] ?? 0 } } }))} unite={unite} />}
            <TableauDetail testId="table-rejets" titre="Rejets détaillés" data={detailRejets.data}
              page={page} setPage={setPage} recherche={recherche} setRecherche={setRecherche}
              colonnes={[
                { cle: "date", label: "Date" }, { cle: "horodatage", label: "Horodatage" },
                { cle: "ligne", label: "Machine" }, { cle: "of", label: "OF" }, { cle: "produit", label: "Produit" },
                { cle: "motif", label: "Motif" },
                { cle: "quantite", label: "Quantité", align: "right", render: (i) => `${fmtNum(i.quantite)} ${i.unite}` },
                { cle: "quart", label: "Quart" }, { cle: "operateur", label: "Opérateur" },
                { cle: "commentaire", label: "Commentaire" }]} />
          </>)}

          {onglet === "of" && ofs.data && (
            <TableauDetail testId="table-of" titre={`Ordres de fabrication (${ofs.data.total})`}
              data={{ items: ofs.data.lignes, total: ofs.data.total, pages: 1 }}
              page={1} setPage={() => {}} recherche={recherche} setRecherche={setRecherche}
              colonnes={[
                { cle: "numero", label: "N° OF" }, { cle: "produit", label: "Produit" }, { cle: "ligne", label: "Machine" },
                { cle: "debut", label: "Début" }, { cle: "fin", label: "Fin" },
                { cle: "duree_sec", label: "Durée", align: "right", render: (l) => fmtDuree(l.duree_sec) },
                { cle: "objectif", label: "Objectif", align: "right", render: (l) => `${fmtNum(l.objectif)} ${l.unite}` },
                { cle: "bonnes", label: "Production bonne", align: "right", render: (l) => `${fmtNum(l.bonnes)} ${l.unite}` },
                { cle: "rejets", label: "Rejets", align: "right", render: (l) => `${fmtNum(l.rejets)} ${l.unite}` },
                { cle: "disponibilite", label: "D", align: "right", render: (l) => fmtPct(l.disponibilite) },
                { cle: "performance", label: "P", align: "right", render: (l) => fmtPct(l.performance) },
                { cle: "qualite", label: "Q", align: "right", render: (l) => fmtPct(l.qualite) },
                { cle: "trs", label: "TRS", align: "right", render: (l) => fmtPct(l.trs) },
                { cle: "statut", label: "Statut" }]} />
          )}

          <Panel title="Export rapide" testId="export-rapide" bodyClassName="p-3 space-y-2">
            <p className="text-[11px] text-muted-foreground">
              Les exports reprennent exactement l'onglet et les filtres actifs : période, machine, produit, quart, OF, catégorie, type d'arrêt, motif de rejet.
            </p>
            <BarreExports filtres={filtres} onglet={onglet} />
          </Panel>
        </>)}
      </>)}
    </div>
  );
}
